import {useEffect} from 'react';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param {Function} setItem - State updater function. The model must include the "date" attribute if `modelItem` is not provided.
 *
 * @example
 * const MODEL_ITEM = {
 *     date: new Date()
 * }
 */
export function useDailyTrigger({setItem, modelItem=''}) {

    /**
     * Configuration of the time for the scheduled execution.
     *
     * - `hours`: hour of the day in 24h format (0-24). 24 represents midnight of the next day.
     * - `min`: minutes (0-59).
     * - `sec`: seconds (0-59).
     * - `ms`: milliseconds (0-999).
     *
     * Used to calculate the remaining time until the next execution.
     */
    const TIME_CONFIG = {
        hours: 24,
        min: 0,
        sec: 0,
        ms: 0
    }

    /**
     * Generates an object with the current date based on the value of `modelItem`.
     *
     * - If `modelItem` has a non-empty value, the object key will be the value of `modelItem`.
     * - If `modelItem` is empty or null, the key `date` will be used.
     *
     */
    const getItemModel = () => {
        if(modelItem !== '' && modelItem ){
            return {
                [modelItem]: new Date()
            }
        }
        return {
            date: new Date()
        }
    }

    /**
     * Calculates the milliseconds remaining until the next configured time.
     *
     */
    const getMsUntilNextTime = () => {
        const now = new Date();
        const nextTime = new Date(now);
        nextTime.setHours(TIME_CONFIG.hours, TIME_CONFIG.min, TIME_CONFIG.sec, TIME_CONFIG.ms);
        if (now >= nextTime) {
            nextTime.setDate(nextTime.getDate() + 1);
        }
        return nextTime - now;
    };

    /**
     * useEffect that schedules the daily execution of the hook.
     *
     * - Initializes a timeout that triggers exactly at the configured time.
     * - When triggered, updates the `setItem` state with the current date.
     * - Automatically schedules the next timeout for the following day.
     * - Clears the timeout when the component unmounts to prevent memory leaks.
     */
    useEffect(() => {
        const triggerNextTime = () => {
            setItem(prev => ({ ...prev, ...getItemModel() }));
            timeoutId = setTimeout(triggerNextTime, getMsUntilNextTime());
        };

        let timeoutId = setTimeout(triggerNextTime, getMsUntilNextTime());

        return () => clearTimeout(timeoutId);
    }, []);

}
